import { FunnelIcon } from '@heroicons/react/20/solid';
import { useEffect, useRef, useState } from 'react';

import { DashboardFilterValues, FillStatus, FilterOptions } from '../api/types';

// The calendar "Show" control: a button that opens a popover for narrowing
// the week by client, skill, region, fill-rate band and shift type. Selecting
// an option applies immediately (parent owns the filter state, which feeds the
// calendar_week query key). Matches the Figma "Show" pill button; the popover
// itself isn't specified in the Figma, so it uses the shared design tokens.

type Props = {
  options?: FilterOptions;
  value: DashboardFilterValues;
  onChange: (next: DashboardFilterValues) => void;
};

const activeCount = (v: DashboardFilterValues): number =>
  v.customerIds.length +
  v.skillIds.length +
  v.regionIds.length +
  v.jobType.length +
  v.fillStatus.length;

const toggle = <T,>(list: T[], item: T): T[] =>
  list.includes(item) ? list.filter((x) => x !== item) : [...list, item];

const ShowFilter = ({ options, value, onChange }: Props) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const count = activeCount(value);

  const group = (
    title: string,
    items: {
      key: string;
      label: string;
      checked: boolean;
      onToggle: () => void;
    }[],
  ) => {
    if (items.length === 0) return null;
    return (
      <div style={{ padding: '8px 0' }}>
        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: 'var(--subtext)',
            padding: '4px 12px',
            textTransform: 'uppercase',
          }}
        >
          {title}
        </div>
        {items.map((it) => (
          <label
            key={it.key}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 12px',
              fontSize: 14,
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={it.checked}
              onChange={it.onToggle}
            />
            {it.label}
          </label>
        ))}
      </div>
    );
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        className="dashv2-showbtn"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <FunnelIcon />
        Show{count > 0 ? ` (${count})` : ''}
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: 44,
            right: 0,
            width: 280,
            maxHeight: 420,
            overflowY: 'auto',
            background: 'var(--white)',
            border: '1px solid var(--stroke-gray)',
            borderRadius: 12,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            zIndex: 20,
            padding: '4px 0',
          }}
        >
          {group(
            'Fill rate',
            (options?.fill_rate_bands ?? []).map((b) => ({
              key: b.value,
              label: b.label,
              checked: value.fillStatus.includes(b.value as FillStatus),
              onToggle: () =>
                onChange({
                  ...value,
                  fillStatus: toggle(value.fillStatus, b.value as FillStatus),
                }),
            })),
          )}
          {group(
            'Shift type',
            (options?.shift_types ?? []).map((t) => ({
              key: t.value,
              label: t.label,
              checked: value.jobType.includes(t.value),
              onToggle: () =>
                onChange({ ...value, jobType: toggle(value.jobType, t.value) }),
            })),
          )}
          {group(
            'Client',
            (options?.clients ?? []).map((c) => ({
              key: `c${c.id}`,
              label: c.name ?? `#${c.id}`,
              checked: value.customerIds.includes(c.id),
              onToggle: () =>
                onChange({
                  ...value,
                  customerIds: toggle(value.customerIds, c.id),
                }),
            })),
          )}
          {group(
            'Skill',
            (options?.skills ?? []).map((s) => ({
              key: `s${s.id}`,
              label: s.name ?? `#${s.id}`,
              checked: value.skillIds.includes(s.id),
              onToggle: () =>
                onChange({ ...value, skillIds: toggle(value.skillIds, s.id) }),
            })),
          )}
          {group(
            'Region',
            (options?.regions ?? []).map((r) => ({
              key: `r${r.id}`,
              label: r.name ?? `#${r.id}`,
              checked: value.regionIds.includes(r.id),
              onToggle: () =>
                onChange({
                  ...value,
                  regionIds: toggle(value.regionIds, r.id),
                }),
            })),
          )}
        </div>
      )}
    </div>
  );
};

export default ShowFilter;
